/**
 * rPI Flow — Workflow Dashboard Page
 *
 * Tab-based workflow operations dashboard. Entry point for
 * /integrations/flow route. Uses manual tab buttons (no @kgc/ui Tabs).
 */
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { FlowApprovalsTab } from './flow/FlowApprovalsTab';
import { FlowCatalogTab } from './flow/FlowCatalogTab';
import { FlowHistoryTab } from './flow/FlowHistoryTab';
import { FlowOperationsTab } from './flow/FlowOperationsTab';
import { FlowRunningTab } from './flow/FlowRunningTab';
import { FlowHealthWidget } from './flow/components/FlowHealthWidget';
import { useApprovals } from './flow/hooks/useApprovals';
import { useExecutionToasts } from './flow/hooks/useExecutionToasts';
import { useExecutions } from './flow/hooks/useExecutions';
import { useWorkflows } from './flow/hooks/useWorkflows';
import type { FlowTab } from './flow/types';

const TAB_IDS: FlowTab[] = ['catalog', 'running', 'history', 'approvals', 'operations'];

export function FlowPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<FlowTab>('catalog');

  const { workflows, isLoading, error, refetch: refetchWorkflows } = useWorkflows();
  const { executions, refetch: refetchExecutions } = useExecutions({
    refetchInterval: activeTab === 'running' ? 10_000 : 30_000,
  });
  const { approvals, refetch: refetchApprovals } = useApprovals();

  const runningCount = executions.filter(
    e => e.status === 'running' || e.status === 'pending'
  ).length;
  const pendingApprovalCount = approvals.filter(
    a => a.status === 'pending' || a.status === 'requested'
  ).length;

  useExecutionToasts(executions, setActiveTab);

  function handleRefresh() {
    void Promise.all([refetchWorkflows(), refetchExecutions(), refetchApprovals()]);
  }

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4 kgc-card-bg dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg text-white font-bold"
            style={{ backgroundColor: '#2D7D8A' }}
          >
            F
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">rPI Flow</h1>
            <p className="text-sm text-muted-foreground">{t('integration.flow.subtitle')}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="flow-refresh-btn">
          {t('integration.refresh')}
        </Button>
      </div>

      {/* Health Widget */}
      <div className="border-b px-4 py-2 dark:border-slate-700">
        <FlowHealthWidget
          workflowCount={workflows.length}
          runningCount={runningCount}
          pendingApprovalCount={pendingApprovalCount}
          scheduleCount={0}
          isOnline={!error}
          onNavigate={setActiveTab}
        />
      </div>

      {/* Tabs */}
      <div className="flex border-b dark:border-slate-700" role="tablist" aria-label="Flow tabs">
        {TAB_IDS.map(id => (
          <button
            key={id}
            id={`flow-tab-${id}`}
            role="tab"
            aria-selected={activeTab === id}
            aria-controls={`flow-tabpanel-${id}`}
            data-testid={`flow-tab-${id}`}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-colors',
              activeTab === id
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setActiveTab(id)}
          >
            {t(`integration.flow.tabs.${id}`)}
            {id === 'running' && runningCount > 0 && (
              <span className="ml-1" aria-live="polite">
                ({runningCount})
              </span>
            )}
            {id === 'approvals' && pendingApprovalCount > 0 && (
              <span className="ml-1" aria-live="polite">
                {'\u{1F534}'}
                {pendingApprovalCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto p-4">
        <div
          id={`flow-tabpanel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`flow-tab-${activeTab}`}
        >
          {activeTab === 'catalog' && (
            <FlowCatalogTab
              workflows={workflows}
              isLoading={isLoading}
              error={error}
              onRetry={handleRefresh}
              onTabChange={setActiveTab}
            />
          )}
          {activeTab === 'running' && (
            <FlowRunningTab executions={executions} onTabChange={setActiveTab} />
          )}
          {activeTab === 'history' && (
            <FlowHistoryTab
              executions={executions}
              workflows={workflows}
              onTabChange={setActiveTab}
            />
          )}
          {activeTab === 'approvals' && (
            <FlowApprovalsTab approvals={approvals} onResolved={() => void refetchApprovals()} />
          )}
          {activeTab === 'operations' && (
            <FlowOperationsTab workflows={workflows} executions={executions} isOnline={!error} />
          )}
        </div>
      </div>
    </div>
  );
}
